type Entry = { value?: unknown; hasValue: boolean; updatedAt: number; pending?: Promise<unknown> };
type CacheEvent='invalidate'|'session'|'denied';

/** Short-lived, per-user memory cache. Never persists article data to browser storage. */
export class QueryCache {
  private entries = new Map<string, Entry>();
  private revision = 0;
  private session = 0;
  private listeners=new Set<(event:CacheEvent)=>void>();
  get sessionEpoch(){return this.session}
  subscribe=(listener:(event:CacheEvent)=>void)=>{this.listeners.add(listener);return()=>{this.listeners.delete(listener)}};
  private notify(event:CacheEvent){for(const listener of this.listeners)listener(event)}
  constructor(private ttl = 30_000, private limit = 100) {}

  peek<T>(key: string): { data?: T; fresh: boolean } {
    const entry = this.entries.get(key);
    return { data: entry?.hasValue ? entry.value as T : undefined, fresh: !!entry?.hasValue && Date.now() - entry.updatedAt < this.ttl };
  }

  invalidate() { this.revision++; this.entries.clear(); this.notify('invalidate'); }
  clearSession() { this.session++; this.revision++;this.entries.clear();this.notify('session'); }
  denyAccess() { this.session++;this.revision++;this.entries.clear();this.notify('denied'); }

  async read<T>(key: string, fetcher: () => Promise<T>, force = false): Promise<T> {
    const previous = this.entries.get(key);
    if (previous?.pending) return previous.pending as Promise<T>;
    if (!force && this.peek(key).fresh) return previous!.value as T;
    const entry: Entry = previous ?? { hasValue: false, updatedAt: 0 };
    const revision = this.revision, session = this.session;
    this.entries.delete(key);
    this.entries.set(key, entry);
    while (this.entries.size > this.limit) this.entries.delete(this.entries.keys().next().value!);
    const pending = fetcher().then(value => {
      if (session !== this.session) throw new Error('Din session er ændret. Hent indholdet igen.');
      // A save completed during this read: do not put the older response back in the cache.
      if (revision !== this.revision) return this.read(key, fetcher, true);
      entry.value = value; entry.hasValue = true; entry.updatedAt = Date.now();
      return value;
    }).finally(() => { if (entry.pending === pending) entry.pending = undefined; });
    entry.pending = pending;
    return pending;
  }
}

export const queryCache = new QueryCache();
